/**
 * Auth Redirect Component
 *
 * Redirects authenticated users away from auth pages (/login, /register)
 * - If user IS authenticated → Redirect to /chat
 * - If user is NOT authenticated → Render the wrapped component
 *
 * This prevents logged-in users from accessing login/register pages.
 */

import React from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import Spinner from './Spinner';
import { getOnboardingGate, approvalRequired } from '../utils/onboardingGate';

const AuthRedirect = ({ children }) => {
  const { isAuthenticated, loading, user } = useAuth();

  // Show loader while checking authentication status
  if (loading) {
    return <Spinner size="md" centered />;
  }

  // If user is authenticated, redirect based on their status
  if (isAuthenticated && user) {
    // Check if user has complete profile and is pending
    const hasPhone = user.phone_number && user.phone_number.length > 0;
    const hasSmsConsent = user.has_sms_consent === true;
    // Photo is optional during registration (see PHOTO_ENABLED in
    // ProfileCompletionModal.js) — don't gate on the image, or pending users
    // without a photo never reach /pending-approval.
    const isProfileComplete = hasPhone && hasSmsConsent;
    const isPending = user.status === 'pending';

    // Redirect pending users with complete profile to pending approval page —
    // unless the onboarding gate turned admin approval off (A/B flow).
    if (isProfileComplete && isPending && approvalRequired(getOnboardingGate(user))) {
      return <Navigate to="/pending-approval" replace />;
    }

    // Otherwise redirect to chat
    return <Navigate to="/chat" replace />;
  }

  // If user is not authenticated, render the login/register page
  return children;
};

export default AuthRedirect;
